import type * as React from "react";
import type { Load } from "@/screens/hooks";

export function Page({ title, children, actions }: { title: string; children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-8 md:py-8">
      <div className="flex flex-wrap items-baseline justify-between gap-3 pb-6">
        <h1 className="text-xl font-medium tracking-title">{title}</h1>
        {actions}
      </div>
      {children}
    </div>
  );
}

export function LoadState<T>({ load, empty, children }: { load: Load<T>; empty?: (data: T) => React.ReactNode; children: (data: T) => React.ReactNode }) {
  if (load.state === "loading") return <div className="h-40 animate-pulse rounded-lg border border-line bg-surface" aria-hidden />;
  if (load.state === "error")
    return (
      <p role="alert" className="text-sm text-err">
        {load.message}
      </p>
    );
  const blank = empty?.(load.data);
  return <>{blank ?? children(load.data)}</>;
}

export function EmptyNote({ title, body }: { title: string; body: string }) {
  return (
    <section className="rounded-lg border border-dashed border-line-strong px-6 py-14 text-center">
      <h2 className="text-md font-medium">{title}</h2>
      <p className="mt-1 text-sm text-ink-2">{body}</p>
    </section>
  );
}

export function Problem({ text }: { text: string | null }) {
  return text ? (
    <p role="alert" className="text-sm text-err">
      {text}
    </p>
  ) : null;
}

export function Table({ head, children, label }: { head: React.ReactNode; children: React.ReactNode; label: string }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table aria-label={label} className="w-full text-left text-sm">
        <thead className="text-xs text-ink-3">
          <tr className="border-b border-line [&>th:first-child]:pl-4">{head}</tr>
        </thead>
        <tbody className="[&>tr]:border-b [&>tr]:border-line [&>tr:last-child]:border-0 [&>tr>td:first-child]:pl-4">{children}</tbody>
      </table>
    </div>
  );
}
